import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Award, Download, Lock, Loader2, ExternalLink, GraduationCap } from 'lucide-react';
import { apiCall } from './api';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/academy`;

/**
 * Vue "Mes certificats" — niveau FORMATION (et non plus par module).
 * Un certificat est délivré quand TOUS les modules d'une formation sont à 100%.
 * Le même composant marche pour Anglais, Marketing, IA, Business, etc.
 */
export default function Certificates({ token, onBack }) {
  const [formations, setFormations] = useState([]);
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const data = await apiCall('/formations', 'GET', null, token);
      setFormations(data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { reload(); }, [token]);

  const issueAndDownload = async (slug) => {
    setBusy(b => ({ ...b, [slug]: true }));
    try {
      const cert = await apiCall(`/certificates/formation/${slug}/issue`, 'POST', {}, token);
      // Refresh list to capture issued state
      reload();
      // Trigger PDF download
      const r = await fetch(`${API}/certificates/${cert.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Erreur lors du téléchargement');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificat-${cert.serial}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Certificat ${cert.serial} téléchargé !`);
    } catch (e) { toast.error(e.message); }
    setBusy(b => ({ ...b, [slug]: false }));
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto px-4 py-8" data-testid="certificates-page">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-4 flex items-center gap-1"><ChevronLeft size={16} />Retour</button>
      <div className="flex items-center gap-2 mb-2"><Award size={20} className="text-yellow-300" /><h2 className="text-2xl font-bold">Mes certificats</h2></div>
      <p className="text-white/60 text-sm mb-6">
        Un certificat <span className="text-emerald-300 font-semibold">officiel JoSmartTech</span> est délivré une fois la formation complète terminée
        (tous les modules à 100%). Tu peux ensuite le télécharger en PDF avec ton numéro de série unique et un QR code de vérification.
      </p>

      {loading && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-emerald-400" size={28} /></div>}

      <div className="grid sm:grid-cols-2 gap-4">
        {formations.map(f => (
          <FormationCert
            key={f.slug}
            f={f}
            isBusy={!!busy[f.slug]}
            onDownload={() => issueAndDownload(f.slug)}
          />
        ))}
        {!loading && formations.length === 0 && (
          <div className="text-white/40 text-sm col-span-2">Aucune formation disponible pour le moment.</div>
        )}
      </div>
    </motion.div>
  );
}

function FormationCert({ f, isBusy, onDownload }) {
  const earned = f.earned;
  const issued = !!f.issued_certificate_id;
  return (
    <div data-testid={`formation-cert-${f.slug}`}
      className={`rounded-2xl p-5 border ${earned ? 'bg-gradient-to-br from-yellow-500/20 to-emerald-500/10 border-yellow-400/40' : 'bg-white/5 border-white/10'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-3xl">{f.icon || '🎓'}</div>
        {earned ? (
          <span className="text-xs bg-yellow-400 text-slate-950 font-bold px-2 py-1 rounded-full">Obtenu</span>
        ) : (
          <Lock size={14} className="text-white/40" />
        )}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-emerald-300/70 mb-0.5">{f.category || 'formation'}</div>
      <h3 className="font-bold text-base">{f.title_fr}</h3>
      {f.description && <p className="text-xs text-white/50 mt-1">{f.description}</p>}

      <div className="text-xs text-white/60 mt-3 flex items-center gap-1.5">
        <GraduationCap size={12} />
        {f.modules_done} / {f.modules_total} modules complétés
        <span className="text-white/30">·</span>
        <span>{f.lessons_completed} / {f.lessons_total} leçons</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mt-2 mb-3">
        <div className={`h-full ${earned ? 'bg-yellow-400' : 'bg-emerald-400'}`} style={{ width: `${f.percent}%` }} />
      </div>
      <div className="text-[10px] text-white/50 mb-3">{f.percent}%</div>

      {earned ? (
        <>
          {issued && (
            <div className="bg-slate-900/40 border border-white/10 rounded-lg p-2 text-[10px] mb-2">
              <div className="text-white/50">N° de série</div>
              <div className="font-mono text-yellow-300">{f.issued_serial}</div>
              <a href={`/academy/verify/${f.issued_certificate_id}`} target="_blank" rel="noreferrer" className="text-emerald-300 hover:underline flex items-center gap-1 mt-1">
                Page de vérification publique <ExternalLink size={10} />
              </a>
            </div>
          )}
          <button onClick={onDownload} disabled={isBusy}
            className="w-full bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-slate-950 font-bold py-2 rounded-lg text-xs flex items-center justify-center gap-1"
            data-testid={`download-formation-cert-${f.slug}`}>
            {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            {issued ? 'Re-télécharger PDF' : 'Générer & télécharger PDF'}
          </button>
        </>
      ) : (
        <div className="text-[10px] text-white/40 italic">
          Termine tous les modules de cette formation pour débloquer ton certificat officiel.
        </div>
      )}
    </div>
  );
}
